/**
 * Preferences questionnaire page — shared live Q UI (full flow or single-type edit).
 *
 * Belongs here: page layout, progress bar, question block wiring.
 * Does not belong here: flow rules or API fetch (see flow-engine/).
 */
import { useEffect } from 'react'
import { PrimaryButton } from '../../../../../components/execution/PrimaryButton'
import { getBottomPadding } from '../../../../../utils/sessionStorage'
import { EventNames } from '../../../../../types/misc.types'
import { lightImpactVibrate, trackEvent } from '../../../../../utils/appBridge'
import { useExpertReviewNavigation } from '../../navigation/expertReviewNavigation'
import { usePreferencesContext } from '../flow-engine/PreferencesProvider'
import { PreferencesQuestionnaireScope } from '../setup/types'
import { usePreferencesQuestionnaireFlow } from '../flow-engine/usePreferencesQuestionnaireFlow'
import {
  computePreferencesProgressValue,
  isCurrentQuestionAnswered,
  getPreferencesNextButtonLabel,
  getPreferenceTypeHeaderStyle
} from '../setup/config'
import { PreferencesPreferenceTypeHeader } from '../ui/header/PreferenceTypeHeader'
import { PreferencesQuestionBlock } from '../ui/questions/QuestionBlock'
import { getPreferenceInsightCopy } from '../ui/questions/PreferenceInsightQuestion'
import { DirectionalSlideSwap } from '../../shared/transitions/DirectionalSlideSwap'
import { isSegmentIntroQuestion } from '../../question-utils/preferencesMappers'
import {
  ExpertReviewScreen,
  expertReviewOptionIdsResponseName,
  trackExpertReviewQuestionEvent
} from '../../analytics/expertReviewEvents'

const CONTENT_SHELL_CLASS =
  'bg-white flex flex-col max-w-2xl mx-auto px-5 pt-6 overflow-hidden'

export interface PreferencesQuestionnairePageContentProps {
  scope: PreferencesQuestionnaireScope
  preferenceTypeId?: string
}

export function PreferencesQuestionnairePageContent({
  scope,
  preferenceTypeId
}: PreferencesQuestionnairePageContentProps) {
  const { goToPreferencesList } = useExpertReviewNavigation()
  const {
    draftAnswers,
    updateDraftAnswer,
    clearAnswer,
    clearSession,
    getVisibleQuestionsForPreferenceType
  } = usePreferencesContext()

  const { progress, handlers } = usePreferencesQuestionnaireFlow(scope)

  const isQuestionnaireReady =
    progress.currentPreferenceType != null &&
    (scope !== PreferencesQuestionnaireScope.Single ||
      (preferenceTypeId != null && progress.currentPreferenceType.id === preferenceTypeId))

  useEffect(() => {
    if (!isQuestionnaireReady) return
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: { ScreenName: ExpertReviewScreen.PreferencesQuestionnaire }
    })
  }, [isQuestionnaireReady])

  if (scope === PreferencesQuestionnaireScope.Single) {
    if (!preferenceTypeId || progress.currentPreferenceType?.id !== preferenceTypeId) {
      return null
    }
  }

  if (!progress.currentPreferenceType) {
    if (!progress.isSingleType) {
      // Leave answering without committing — drop dirty draft before leaving Q route
      clearSession()
      goToPreferencesList({ replace: true })
    }
    return null
  }

  const testLocatorBack = progress.isSingleType
    ? 'preferences-type-questionnaire-back'
    : 'preferences-questionnaire-back'
  const testLocatorMenu = progress.isSingleType
    ? 'preferences-type-questionnaire-menu'
    : 'preferences-questionnaire-menu'
  const testLocatorNext = progress.isSingleType
    ? 'preferences-type-questionnaire-next'
    : 'preferences-questionnaire-next'

  const progressValue = computePreferencesProgressValue(
    progress.preferenceTypes,
    progress.preferenceTypeIndex,
    progress.questionIndex,
    progress.visibleCountInCurrentType,
    progress.isSingleType,
    (typeId) => ({ length: getVisibleQuestionsForPreferenceType(typeId).length })
  )

  const contentBottomPadding = getBottomPadding() + 16

  if (!progress.currentQuestionResolved) {
    return (
      <div className='flex h-screen flex-col min-h-0 bg-white'>
        <div className='shrink-0'>
          <PreferencesPreferenceTypeHeader
            preferenceType={progress.currentPreferenceType}
            progressValue={progressValue}
            onBack={handlers.handleBack}
            testLocatorBack={testLocatorBack}
            testLocatorMenu={testLocatorMenu}
          />
        </div>
        <div
          style={{ paddingBottom: `${contentBottomPadding}px` }}
          className='flex flex-1 min-h-0 overflow-y-auto no-scrollbar px-5 pt-4'
        >
          <div className='rounded-[16px] border border-[#E5E7EB] flex flex-1 min-h-0 items-center justify-center bg-white'>
            <p className='text-gray-600'>No question</p>
          </div>
        </div>
      </div>
    )
  }

  const currentQuestionInputType = progress.currentQuestionResolved.input?.type
  const isInsight = isSegmentIntroQuestion(progress.currentQuestionResolved)
  const pageBackground = isInsight
    ? getPreferenceTypeHeaderStyle(progress.currentPreferenceType.id).background
    : undefined

  const showNextButton =
    isInsight ||
    (currentQuestionInputType !== 'AMOUNT' && currentQuestionInputType !== 'SINGLE_SELECT')
  const isNextDisabled = !isCurrentQuestionAnswered(
    progress.currentQuestionResolved.id,
    draftAnswers,
    currentQuestionInputType,
    progress.currentQuestionResolved.questionType
  )
  const preferenceTypeIdForAnalytics = progress.currentPreferenceType.id
  const nextButtonLabel = getPreferencesNextButtonLabel(
    currentQuestionInputType,
    progress.isComplete,
    getPreferenceInsightCopy(preferenceTypeIdForAnalytics).nextButtonLabel,
    progress.currentQuestionResolved.questionType
  )

  const shellClassName = isInsight
    ? 'flex flex-col max-w-2xl mx-auto px-5 pt-8 flex-1 min-h-0 overflow-x-hidden overflow-y-auto no-scrollbar'
    : `${CONTENT_SHELL_CLASS} flex-1 min-h-0 overflow-x-hidden overflow-y-auto no-scrollbar`

  return (
    <div
      className='flex h-screen flex-col min-h-0 bg-white relative overflow-hidden'
      style={{
        paddingBottom: `${contentBottomPadding}px`,
        ...(pageBackground ? { background: pageBackground } : {})
      }}
    >
      {isInsight ? (
        <img
          src='/images/allocation/pref_header.png'
          alt=''
          className='pointer-events-none absolute bottom-0 right-[-88px] h-[241px] w-[257px] z-0 opacity-40 mix-blend-color-burn'
        />
      ) : null}
      <div className='shrink-0 relative z-[1]'>
        <PreferencesPreferenceTypeHeader
          preferenceType={progress.currentPreferenceType}
          question={progress.currentQuestionResolved}
          progressValue={progressValue}
          onBack={handlers.handleBack}
          testLocatorBack={testLocatorBack}
          testLocatorMenu={testLocatorMenu}
        />
      </div>

      <div className='flex-1 min-h-0 overflow-y-auto no-scrollbar relative z-[1]'>
        <div className={shellClassName} style={{ paddingBottom: `${contentBottomPadding}px` }}>
          <DirectionalSlideSwap
            contentKey={progress.currentQuestionResolved.id}
            direction={progress.slideDirection}
          >
            <PreferencesQuestionBlock
              question={progress.currentQuestionResolved}
              answers={draftAnswers}
              onAdvance={handlers.handleAnswerAndAdvance}
              updateDraftAnswer={updateDraftAnswer}
              onAmountClear={
                progress.currentQuestionId != null
                  ? () => clearAnswer(progress.currentQuestionId!)
                  : undefined
              }
              amountHelperText='This value will be included in your total portfolio for allocation review'
              contextId={progress.currentPreferenceType.id}
            />
          </DirectionalSlideSwap>
        </div>
      </div>

      {showNextButton ? (
        <div className='shrink-0 mt-6 px-5 relative z-[1]'>
          <PrimaryButton
            testLocator={testLocatorNext}
            label={nextButtonLabel}
            onClick={() => {
              lightImpactVibrate()
              const questionId = progress.currentQuestionResolved.id
              const answerValue = draftAnswers[questionId]
              // Commit: multi-select → all option ids; single option → one id; amount via Next is unused (amount uses Confirm)
              const responseName = Array.isArray(answerValue)
                ? expertReviewOptionIdsResponseName(answerValue.map(String))
                : typeof answerValue === 'string' && answerValue !== ''
                  ? answerValue
                  : undefined
              const amount =
                typeof answerValue === 'number' && !Number.isNaN(answerValue)
                  ? answerValue
                  : undefined
              trackExpertReviewQuestionEvent({
                eventName: EventNames.ButtonTap,
                ScreenName: ExpertReviewScreen.PreferencesQuestionnaire,
                questionId,
                scrollSection: preferenceTypeIdForAnalytics,
                responseName,
                amount
              })
              handlers.handleNext()
            }}
            primaryButtonClassName='w-full !h-[56px] !font-[500] !rounded-[16px]'
            isDisabled={isNextDisabled}
          />
        </div>
      ) : null}
    </div>
  )
}

export default function AllocationPreferencesQuestionnairePage() {
  const {
    preferenceTypes,
    isPreferenceTypesLoading,
    isPreferenceTypesError,
    cancelAnswering,
    clearSession
  } = usePreferencesContext()
  const { goToPreferencesList } = useExpertReviewNavigation()

  if (isPreferenceTypesLoading) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-white px-5 py-16'>
        <p className='text-gray-600'>Loading…</p>
      </div>
    )
  }

  if (isPreferenceTypesError) {
    return (
      <div className='flex min-h-screen flex-col items-center justify-center bg-white px-5 py-16 gap-4'>
        <p className='text-gray-600'>Failed to load preferences</p>
        <button type='button' className='text-blue-600' onClick={cancelAnswering}>
          Go back
        </button>
      </div>
    )
  }

  if (preferenceTypes.length === 0) {
    clearSession()
    goToPreferencesList({ replace: true })
    return null
  }

  return <PreferencesQuestionnairePageContent scope={PreferencesQuestionnaireScope.All} />
}
